<template>
    <TprmModal
        additional-class-name="CustomPropertyModal"
        :open="true"
        :title="property ? 'Edit Custom Property' : 'Add Custom Property'"
        size="md"
        :allow-close="!submitting"
        @close="$emit('close')"
    >
        <Alert v-if="error" class="CustomPropertyModal__errorBanner" type="danger" :title="error" />
        <div class="CustomPropertyModal__inputSection flex column gap-4">
            <TextInput
                id="CustomPropertyModal__name"
                class="CustomPropertyModal__name"
                label="Property Name"
                :value="form.name"
                placeholder="Enter property name"
                :required="true"
                :disabled="submitting"
                @input="(e) => form.name = e.target.value"
            />

            <TextInput
                id="CustomPropertyModal__description"
                class="CustomPropertyModal__description"
                label="Description"
                :value="form.description"
                placeholder="Enter description (optional)"
                :disabled="submitting"
                @input="(e) => form.description = e.target.value"
            />

            <EnumInput
                id="CustomPropertyModal__fieldType"
                class="CustomPropertyModal__fieldType"
                label="Field Type"
                :value="form.fieldType || ''"
                :options="fieldTypeOptions"
                :required="!property"
                :disabled="submitting || !!property"
                @input="(val) => form.fieldType = val"
            />

            <EnumOptionsInput
                v-if="form.fieldType === CustomPropertyType.enum"
                ref="enumOptionsInput"
                :value="form.enumOptions"
                :usage-counts="usageCounts"
                :loading-usage="loadingUsage"
                :disabled="submitting"
                :reorder-saving="reorderSaving"
                @input="(val) => form.enumOptions = val"
                @reorder="onEnumOptionsReorder"
            />

            <CheckboxInput
                id="CustomPropertyModal__required"
                class="CustomPropertyModal__required"
                name="CustomPropertyModal__required"
                label="Required"
                :value="true"
                :model-value="form.required"
                :disabled="submitting"
                @change="(e) => form.required = e.target.checked"
            />
        </div>

        <template #footer>
            <NewButton variant="outline" color="neutral" :disabled="submitting" @click="$emit('close')">
                Cancel
            </NewButton>
            <NewButton
                variant="fill"
                color="primary"
                :loading="submitting"
                :disabled="!isSubmittable || submitting || reorderSaving"
                @click="onSave"
            >
                {{ property ? 'Save Changes' : 'Create Property' }}
            </NewButton>
        </template>
    </TprmModal>
</template>

<style lang="scss">
.CustomPropertyModal {
    &__required .CheckboxInput__text {
        font-weight: normal;
    }

    .Alert {
        margin-bottom: 16px;
        border-radius: var(--primary-border-radius);
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import Alert from '@evidentid/dashboard-commons/components/Alert/Alert.vue';
    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown';
    import { JsonSchemaEnum } from '@evidentid/json-schema/interfaces/JsonSchema';
    import { CustomProperty, CustomPropertyType, EnumOptionUsage } from '@evidentid/tprm-portal-lib/models/dashboard';

    import NewButton from '@/components/new-button/NewButton.vue';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';
    import EnumOptionsInput
        from '@/modules/dashboard-configuration/components/custom-properties-management/EnumOptionsInput/EnumOptionsInput.vue';
    import CheckboxInput from '@/modules/dynamic-form/components/style-inputs/checkbox-input/CheckboxInput.vue';
    import EnumInput from '@/modules/dynamic-form/components/style-inputs/enum-input/EnumInput.vue';
    import TextInput from '@/modules/dynamic-form/components/style-inputs/text-input/TextInput.vue';

    interface EnumOptionsInputRef {
        validate?(): boolean;
    }

    const FIELD_TYPE_OPTIONS: DropdownOption<CustomPropertyType>[] = [
        { label: 'Text', value: CustomPropertyType.string },
        { label: 'Integer', value: CustomPropertyType.integer },
        { label: 'Number', value: CustomPropertyType.number },
        { label: 'Boolean', value: CustomPropertyType.boolean },
        { label: 'Email', value: CustomPropertyType.email },
        { label: 'List Text', value: CustomPropertyType.listString },
        { label: 'List Email', value: CustomPropertyType.listEmail },
        { label: 'Collaterals', value: CustomPropertyType.collaterals },
        { label: 'Equipment', value: CustomPropertyType.equipment },
        { label: 'Credential Types', value: CustomPropertyType.credentialTypes },
        { label: 'Address', value: CustomPropertyType.address },
        { label: 'Dropdown', value: CustomPropertyType.enum },
    ];

    export default defineComponent({
        name: 'CustomPropertyModal',

        components: {
            Alert,
            TprmModal,
            NewButton,
            TextInput,
            EnumInput,
            CheckboxInput,
            EnumOptionsInput,
        },

        props: {
            property: {
                type: Object as PropType<CustomProperty | null>,
                default: null,
            },
            submitting: {
                type: Boolean,
                default: false,
            },
            error: {
                type: String as PropType<string | null>,
                default: null,
            },
            usageCounts: {
                type: Object as PropType<Record<string, EnumOptionUsage> | null>,
                default: null,
            },
            loadingUsage: {
                type: Boolean,
                default: false,
            },
            reorderSaving: {
                type: Boolean,
                default: false,
            },
        },
        emits: [ 'close', 'enum-options-reorder', 'submit' ],

        data() {
            return {
                CustomPropertyType,
                form: {
                    name: '',
                    description: '',
                    fieldType: null as CustomPropertyType | null,
                    required: false,
                    enumOptions: [] as string[],
                },
            };
        },

        computed: {
            fieldTypeOptions(): DropdownOption<CustomPropertyType>[] {
                return [ ...FIELD_TYPE_OPTIONS ].sort((a, b) => a.label.localeCompare(b.label));
            },
            isSubmittable(): boolean {
                const hasName = this.form.name.trim().length > 0;
                const hasType = this.form.fieldType !== null;
                if (this.form.fieldType !== CustomPropertyType.enum) {
                    return hasName && hasType;
                }
                const options = this.form.enumOptions;
                const hasOptions = options.length > 0;
                const noEmpty = options.every((opt) => opt.trim() !== '');
                const noDupes = new Set(options.map((o) => o.trim())).size === options.length;
                return hasName && hasOptions && noEmpty && noDupes;
            },
        },

        created() {
            if (this.property) {
                this.form.name = this.property.name || '';
                this.form.description = this.property.description || '';
                this.form.fieldType = this.property.type || null;
                this.form.required = this.property.required || false;
                this.form.enumOptions = (this.property.schema as JsonSchemaEnum<string>).enum ?? [];
            }
        },

        methods: {
            onSave() {
                if (!this.isSubmittable) {
                    return;
                }
                const enumRef = this.$refs.enumOptionsInput as EnumOptionsInputRef;
                if (enumRef && enumRef.validate && !enumRef.validate()) {
                    return;
                }
                if (this.property) {
                    this.$emit('submit', {
                        name: this.form.name.trim(),
                        description: this.form.description,
                        required: this.form.required,
                        ...(this.property.type === CustomPropertyType.enum && {
                            enumOptions: this.form.enumOptions,
                        }),
                    });
                } else {
                    this.$emit('submit', {
                        name: this.form.name.trim(),
                        description: this.form.description,
                        fieldType: this.form.fieldType,
                        required: this.form.required,
                        ...(this.form.fieldType === CustomPropertyType.enum && {
                            enumOptions: this.form.enumOptions,
                        }),
                    });
                }
            },
            onEnumOptionsReorder(newOrder: string[]) {
                if (this.property) {
                    this.$emit('enum-options-reorder', {
                        newOrder,
                        name: this.form.name.trim(),
                        description: this.form.description,
                        required: this.form.required,
                    });
                }
            },
        },
    });
</script>
